import axios from 'axios';
import { toast } from 'sonner';
import { getOrCreateDeviceId } from '@/utils/deviceId';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  withCredentials: true,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const deviceId = getOrCreateDeviceId();
  if (deviceId) {
    config.headers = config.headers || {};
    config.headers['X-GS-Device-Id'] = deviceId;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('Não autenticado');
    } else if (error.response?.status === 429) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string' && detail.length > 0) {
        toast.error(detail);
      }
    }
    return Promise.reject(error);
  }
);

export const publicAPI = {
  getOpenAccessPromo: () => apiClient.get('/public/open-access-promo'),
};

export const authAPI = {
  register: (email, password, name, referralCode = null) =>
    apiClient.post('/auth/register', {
      email,
      password,
      name,
      ...(referralCode ? { referral_code: String(referralCode).trim() } : {}),
    }),

  login: (email, password) =>
    apiClient.post('/auth/login', { email, password }),

  /** Conclui login quando 2FA está ativo (código por e-mail ou SMS). */
  login2FA: (challengeId, code) =>
    apiClient.post('/auth/login/2fa', { challenge_id: challengeId, code: String(code).trim() }),

  forgotPassword: (email) =>
    apiClient.post('/auth/forgot-password', { email: String(email).trim().toLowerCase() }),

  resetPassword: (token, newPassword) =>
    apiClient.post('/auth/reset-password', { token, new_password: newPassword }),

  resendVerification: () => apiClient.post('/auth/resend-verification'),

  /** enabled, method: 'email'|'sms', phone?, current_password */
  updateTwoFactor: (payload) => apiClient.put('/auth/security/2fa', payload),

  updateProfileName: (name) => apiClient.patch('/auth/profile', { name: String(name).trim() }),

  changePassword: (currentPassword, newPassword) =>
    apiClient.put('/auth/profile/password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  changeEmail: (newEmail, currentPassword) =>
    apiClient.put('/auth/profile/email', {
      new_email: String(newEmail).trim().toLowerCase(),
      current_password: currentPassword,
    }),

  createSession: (sessionId) =>
    apiClient.post('/auth/session', {}, { headers: { 'X-Session-ID': sessionId } }),

  getMe: () => apiClient.get('/auth/me'),

  logout: () => apiClient.post('/auth/logout'),
};

export const teamsAPI = {
  search: (query) => apiClient.get('/teams/search', { params: { q: query } }),
  
  getDetail: (teamId) => apiClient.get(`/teams/${teamId}`),
  
  getPlayers: (teamId, includeStats = false) => apiClient.get(`/teams/${teamId}/players`, { params: { include_stats: includeStats } }),
  
  getPlayerStats: (playerId, teamId, season = 2024) => apiClient.get(`/players/${playerId}/statistics`, { params: { team_id: teamId, season } }),

  /** Um pedido HTTP: várias estatísticas (Player x Player / elenco). */
  batchPlayerStatistics: (body) => apiClient.post('/players/statistics/batch', body),

  getLineup: (teamId) => apiClient.get(`/teams/${teamId}/lineup`),

  /** Próximas e últimas partidas do clube (endpoint fixtures/between). */
  getFixtureSlate: (teamId) => apiClient.get(`/teams/${teamId}/fixture-slate`),

  /** Forma W/D/L + rating por jogo (últimas 5), alinhado ao gráfico de forma. */
  getRecentFormRatings: (teamId) => apiClient.get(`/teams/${teamId}/recent-form-ratings`),
};

export const matchesAPI = {
  getHeadToHead: (team1Id, team2Id) =>
    apiClient.get('/matches/head-to-head', { params: { team1_id: team1Id, team2_id: team2Id } }),
};

export const leaguesAPI = {
  /** Liga ou competição com página /liga/:slug (nome ou slug). */
  search: (q, limit = 12) =>
    apiClient.get('/leagues/search', { params: { q, limit } }),
  /** Resolve season_id (time âncora + liga), igual ao fluxo da análise de confronto. */
  getSeasonId: (slug) => apiClient.get(`/leagues/${encodeURIComponent(slug)}/season-id`),
};

/** Artilheiros por temporada (topscorers/seasons/{id} via backend). */
export const topscorersAPI = {
  getBySeason: (seasonId) =>
    apiClient.get(`/topscorers/season/${seasonId}`),
};

export const standingsAPI = {
  /** focusTeamIds: inclui esses times na tabela quando o backend aplica corte (Pro/Trial). */
  getBySeason: (seasonId, focusTeamIds = null) =>
    apiClient.get(`/standings/season/${seasonId}`, {
      params:
        focusTeamIds && focusTeamIds.length
          ? { focus_team_ids: focusTeamIds.filter(Boolean).join(',') }
          : {},
    }),
  getRoundFixtures: (seasonId, team1Id, team2Id) =>
    apiClient.get(`/standings/season/${seasonId}/round-fixtures`, {
      params: team1Id && team2Id ? { team1_id: team1Id, team2_id: team2Id } : {},
    }),
};

export const fixturesAPI = {
  getUpcoming: (limit = 10, options = {}) =>
    apiClient.get('/fixtures/upcoming', {
      params: {
        limit,
        ...(options.homeCard ? { home_card: 1 } : {}),
        ...(options.perTeamUnique === false ? { per_team_unique: 0 } : {}),
        ...(options.maxDays != null ? { max_days: options.maxDays } : {}),
      },
    }),
};

export const livescoresAPI = {
  getInplay: (limit = 10) =>
    apiClient.get('/livescores', { params: { limit } }),
  getFixtureDetail: (fixtureId) =>
    apiClient.get(`/livescores/${fixtureId}`),
  getEnhanced: (fixtureId) =>
    apiClient.get(`/fixtures/${fixtureId}/enhanced`),
  getBetSuggestions: (fixtureId, { team1Id, team2Id } = {}) =>
    apiClient.get(`/fixtures/${fixtureId}/bet-suggestions`, {
      params: {
        ...(team1Id != null && team2Id != null
          ? { team1_id: team1Id, team2_id: team2Id }
          : {}),
      },
    }),
};

export const refereesAPI = {
  getProfile: (refereeId) =>
    apiClient.get(`/referees/${refereeId}/profile`),
};

export const playersAPI = {
  /** refresh=1 evita resposta em cache (10 min) após alterações no backend */
  getProfile: (playerId, config = {}) =>
    apiClient.get(`/players/${playerId}/profile`, {
      ...config,
      params: { refresh: 1, ...config.params },
    }),
  search: (query, limit = 20) =>
    apiClient.get('/players/search', { params: { q: query, limit } }),
  getInjuries: (teamId) =>
    apiClient.get(`/teams/${teamId}/injuries`),
};

/** 3 IAs em paralelo + síntese Green Stats em sequência */
const AI_ANALYZE_MULTIPLE_TIMEOUT_MS = 240000;

export const aiAPI = {
  analyzeMultiple: (data) =>
    apiClient.post('/ai/analyze-multiple', data, { timeout: AI_ANALYZE_MULTIPLE_TIMEOUT_MS }),
};

export const postsAPI = {
  getAll: (limit = 20) => apiClient.get('/posts', { params: { limit } }),
  
  create: (postData) => apiClient.post('/posts', postData),
  
  like: (postId) => apiClient.post(`/posts/${postId}/like`),
  
  comment: (postId, content) => apiClient.post(`/posts/${postId}/comment`, { content }),
};

export const predictionsAPI = {
  create: (predictionData) => apiClient.post('/predictions', predictionData),
  
  getPublic: (limit = 50) => apiClient.get('/predictions/public', { params: { limit } }),
  
  getUserPredictions: () => apiClient.get('/predictions/user'),
  
  like: (predictionId) => apiClient.post(`/predictions/${predictionId}/like`),
};

export const leaderboardAPI = {
  get: (limit = 20) => apiClient.get('/leaderboard', { params: { limit } }),
};

export const oddsAPI = {
  getBettingOdds: (team1Id, team2Id, fixtureId = null) =>
    apiClient.get(`/betting-odds/${team1Id}/${team2Id}`, {
      params: fixtureId ? { fixture_id: fixtureId } : {},
    }),
  getSuggestedMarkets: (team1Id, team2Id) => apiClient.get(`/suggested-markets/${team1Id}/${team2Id}`),
};

export const betHistoryAPI = {
  list: (limit = 60, status = null) =>
    apiClient.get('/bet-history', { params: { limit, ...(status ? { status } : {}) } }),
  stats: () => apiClient.get('/bet-history/stats'),
  resolve: (betId, scoreH, scoreA, notes = '') =>
    apiClient.post(`/bet-history/${betId}/resolve`, { score_h: scoreH, score_a: scoreA, notes }),
  suggestions: (limit = 20) => apiClient.get('/bets/suggestions', { params: { limit } }),
  autoResolve: () => apiClient.post('/bet-history/auto-resolve'),
  /** Dispara auto-resolve em background (não exige admin). */
  refresh: () => apiClient.post('/bet-history/refresh'),
  deleteVoid: () => apiClient.post('/bet-history/delete-void'),
};

export const adminAPI = {
  getPendingExperts: () => apiClient.get('/admin/pending-experts'),
  approveExpert: (userId) => apiClient.post(`/admin/approve-expert/${userId}`),
  getPlatformStats: () => apiClient.get('/admin/platform-stats'),
  listUsers: (params = {}) => apiClient.get('/admin/users', { params }),
  grantTipster: ({ email, userId, referralCode, networkRecruitCode } = {}) =>
    apiClient.post('/admin/tipster/grant', {
      ...(email ? { email: String(email).trim() } : {}),
      ...(userId ? { user_id: String(userId).trim() } : {}),
      ...(referralCode ? { referral_code: String(referralCode).trim() } : {}),
      ...(networkRecruitCode ? { network_recruit_code: String(networkRecruitCode).trim() } : {}),
    }),
  getRevshareStats: () => apiClient.get('/admin/revshare/stats'),
  getNetworkPartners: () => apiClient.get('/admin/network/partners'),
  getNetworkStats: () => apiClient.get('/admin/network/stats'),
  approveNetworkPartner: (targetUserId, networkCode = null) =>
    apiClient.post(`/admin/network/partners/${String(targetUserId).trim()}/approve`, {
      ...(networkCode ? { network_code: String(networkCode).trim() } : {}),
    }),
};

export const bilhetesAdminAPI = {
  list: (date = null, limit = 50) =>
    apiClient.get('/admin/bilhetes', { params: { ...(date ? { date } : {}), limit } }),
  create: (data) => apiClient.post('/admin/bilhetes', data),
  update: (id, data) => apiClient.put(`/admin/bilhetes/${id}`, data),
  remove: (id) => apiClient.delete(`/admin/bilhetes/${id}`),
  today: () => apiClient.get('/bilhetes/today'),
};

/** GreenStats Network — parceiros que recrutam Tipsters (pedido, link, stats). */
export const networkAPI = {
  apply: () => apiClient.post('/network/apply'),
  me: () => apiClient.get('/network/me'),
  recruitmentStats: () => apiClient.get('/network/me/recruitment-stats'),
};

export const plansAPI = {
  subscribe: (plan) => apiClient.post('/plans/subscribe', { plan }),
  cancel: () => apiClient.post('/plans/cancel'),
};

export const billingAPI = {
  createCheckoutSession: (plan) => apiClient.post('/billing/stripe/create-checkout-session', { plan }),
  createCustomerPortal: () => apiClient.post('/billing/stripe/customer-portal', {}),
  createMercadoPagoPreference: (plan) => apiClient.post('/billing/mercadopago/create-preference', { plan }),
};

/** Notícias agregadas (pré e pós-jogo). */
export const newsAPI = {
  list: (limit = 30) => apiClient.get('/news', { params: { limit } }),
  /** Pré/pós-jogo para o confronto; fixtureId opcional (ex.: vindo dos próximos jogos). */
  confronto: (team1Id, team2Id, fixtureId = null) =>
    apiClient.get('/news/confronto', {
      params: {
        team1_id: team1Id,
        team2_id: team2Id,
        ...(fixtureId != null && fixtureId > 0 ? { fixture_id: fixtureId } : {}),
      },
    }),
};